import { useEffect, useState } from "react";
import { fetchRecommendations } from "../holideviser/libraryApi";

const CATEGORY_LABEL = { EAT: "Eat", DRINK: "Drink" };

// Modal picker that surfaces Knowledge Library records for a section (EAT / DRINK).
export const LibraryPicker = ({ category, onPick, onClose }) => {
  const [recs, setRecs] = useState(null);
  const [destination, setDestination] = useState("");

  useEffect(() => {
    let active = true;
    fetchRecommendations({ category })
      .then((data) => active && setRecs(data))
      .catch(() => active && setRecs([]));
    return () => {
      active = false;
    };
  }, [category]);

  const destinations = recs ? Array.from(new Set(recs.map((r) => r.destination))).sort() : [];
  const filtered = recs ? recs.filter((r) => !destination || r.destination === destination) : [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur"
      data-testid="library-picker"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto border border-charcoal bg-ink p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-[0.6rem] uppercase tracking-[0.28em] text-teal">
              Holideviser Library · {CATEGORY_LABEL[category] || category}
            </p>
            <h3 className="mt-2 font-serif text-2xl font-light text-cream">Add from Holideviser Library</h3>
          </div>
          <button
            onClick={onClose}
            data-testid="library-picker-close"
            className="border border-charcoal px-3 py-1 font-sans text-xs text-sand hover:border-teal hover:text-teal"
          >
            ✕
          </button>
        </div>

        {/* Destination filter */}
        {destinations.length > 1 && (
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={() => setDestination("")}
              className={`border px-3 py-1 font-sans text-xs ${!destination ? "border-teal text-teal" : "border-charcoal text-sand hover:text-cream"}`}
            >
              All
            </button>
            {destinations.map((d) => (
              <button
                key={d}
                onClick={() => setDestination(d)}
                data-testid={`library-dest-${d.toLowerCase().replace(/\s+/g, "-")}`}
                className={`border px-3 py-1 font-sans text-xs ${destination === d ? "border-teal text-teal" : "border-charcoal text-sand hover:text-cream"}`}
              >
                {d}
              </button>
            ))}
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3">
          {recs === null && <p className="font-sans text-sm text-sand">Loading library…</p>}
          {recs !== null && filtered.length === 0 && (
            <p className="font-sans text-sm text-sand">No verified places yet for this category.</p>
          )}
          {filtered.map((rec) => (
            <button
              key={rec.id}
              onClick={() => onPick(rec)}
              data-testid={`library-pick-${rec.id}`}
              className="group flex items-center gap-4 border border-charcoal p-3 text-left transition-colors hover:border-teal"
            >
              {rec.hero_image && (
                <img src={rec.hero_image} alt="" className="h-16 w-16 shrink-0 object-cover" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate font-serif text-lg text-cream">{rec.name}</p>
                  {rec.verification_status === "PERSONALLY_VERIFIED" && (
                    <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-teal" title="Holideviser Verified" />
                  )}
                </div>
                <p className="truncate font-sans text-xs text-sand">
                  {[rec.subcategory, rec.destination].filter(Boolean).join(" · ")}
                </p>
              </div>
              <span className="shrink-0 font-sans text-[0.6rem] uppercase tracking-[0.16em] text-teal opacity-0 transition-opacity group-hover:opacity-100">
                Add →
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
